import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// Services
import { postManagerDirectEdit } from './directEditService';
import { TABLE_QUERY_KEY } from '../table/tableQueries';
import { applyDirectEditCellPatchToTableData } from './directEditCachePatch';

// Utils
import { normalizeUrl } from '../../utils/url';

// Components
import useToast from '../../components/molecules/Toast/useToast';

/**
 * @typedef {import('./directEditCachePatch').DirectEditCellPatch} DirectEditCellPatch
 */

/**
 * @typedef {object} DirectEditMutationVariables
 * @property {string} url - Endpoint to POST the new value to
 * @property {string} value - String value sent to the API (boolean is "oui"/"non", clear is "", selectClass is the option id)
 * @property {string} rowId - Row id of the edited cell (for cache patch while pending)
 * @property {string} columnId - Column id of the edited cell (for cache patch while pending)
 * @property {DirectEditCellPatch} [cellPatch] - Optional patch so the cell shows the submitted value until refetch
 */

/**
 * Mutation hook for direct edit cell value updates.
 * Patches the table cache on mutate so the submitted value is visible while pending,
 * rolls back on error, then invalidates active table queries on settle.
 * @param {import('@tanstack/react-query').UseMutationOptions<any, Error, DirectEditMutationVariables, unknown>} [mutationOptions = {}] - The mutation options
 * @returns {import('@tanstack/react-query').UseMutationResult} The mutation result
 * @example
 * const { mutate, isPending } = useDirectEditCellValueMutation();
 * mutate({ url: setAttributeUrl, value: 'new value', rowId, columnId });
 */
export const useDirectEditCellValueMutation = (mutationOptions = {}) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { t } = useTranslation();

  const {
    onSuccess, onError, onSettled, ...restOptions
  } = mutationOptions;

  return useMutation({
    mutationFn: ({ url, value }) => postManagerDirectEdit(url, {
      value,
      isFirstLoopOccurence: true,
      isLastLoopOccurence: true,
    }),
    ...restOptions,
    onMutate: async ({
      value, rowId, columnId, cellPatch,
    }) => {
      if (!rowId || !columnId) {
        return {};
      }

      /** @type {TableQueryDataTuple[]} */
      const arrayTableData = /** @type {TableQueryDataTuple[]} */ (queryClient.getQueriesData({
        queryKey: [TABLE_QUERY_KEY],
        type: 'active',
      }));

      const snapshots = arrayTableData.map(([queryKey, data]) => ({
        queryKey,
        previousData: data,
      }));

      const fallbackString = value !== undefined ? String(value) : '';

      arrayTableData.forEach(([queryKey, data]) => {
        if (!data) return;
        const patched = applyDirectEditCellPatchToTableData(
          data,
          rowId,
          columnId,
          cellPatch,
          fallbackString,
        );
        queryClient.setQueryData(queryKey, patched);
      });

      await queryClient.cancelQueries({ queryKey: [TABLE_QUERY_KEY], type: 'active' });

      return { snapshots };
    },
    onSuccess: (data, variables, context) => {
      if (data && typeof data === 'object') {
        if (data.openTab?.basicUrl) {
          const normalizedUrl = normalizeUrl(data.openTab.basicUrl);
          window.open(normalizedUrl, '_blank');
        }
        if (typeof data.message === 'string' && data.message.length > 0) {
          notify({ message: data.message, type: 'success' });
        }
      }
      onSuccess?.(data, /** @type {DirectEditMutationVariables} */ (variables), context);
    },
    onError: (error, variables, context) => {
      const snapshots = /** @type {{ snapshots: Array<{ queryKey: import('@tanstack/react-query').QueryKey, previousData: unknown }> } | undefined} */ (
        context
      )?.snapshots;
      snapshots?.forEach(({ queryKey, previousData }) => {
        queryClient.setQueryData(queryKey, previousData);
      });

      notify({
        message: error?.message || t('toast.directEdit.error'),
        type: 'alert',
      });
      onError?.(error, /** @type {DirectEditMutationVariables} */ (variables), context);
    },
    onSettled: async (data, error, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEY], type: 'active' });
      onSettled?.(data, error, /** @type {DirectEditMutationVariables} */ (variables), context);
    },
  });
};
